import {
  AccountInfo,
  InteractionRequiredAuthError,
  AuthError,
} from "@azure/msal-browser";
import { ref, computed, readonly } from "vue";
import { msalService } from "@/services/msal";
import { enableGraphAccess, establishUserSession } from "@/services/api";
import { useToast } from "./useToast";

// --- Global Auth State ---
const userSession = ref<{ name: string | null; email: string | null; isAuthorized: boolean; settings: Record<string, unknown> } | null>(null);
const account = ref<AccountInfo | null>(null);
const isInitialized = ref(false);
const initializationError = ref<AuthError | null>(null);
const isLoading = ref(false); // Global loading state
const lastAuthOperation = ref<string>(""); // Track last auth operation for better UX

// Auth state persistence
const AUTH_STORAGE_KEY = "agentpanels_auth_state";

interface PersistedAuthState {
  hasValidSession: boolean;
  lastSuccessfulAuth: string;
  userEmail?: string;
}

function loadPersistedAuthState(): PersistedAuthState | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn("Failed to load persisted auth state:", error);
    return null;
  }
}

function saveAuthState(state: PersistedAuthState): void {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Failed to persist auth state:", error);
  }
}

function clearPersistedAuthState(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear persisted auth state:", error);
  }
}

// --- Initialization Logic ---
let initializePromise: Promise<void> | null = null;

async function initializeMsal() {
  // Prevent multiple initializations
  if (initializePromise) {
    return initializePromise;
  }

  initializePromise = (async () => {
    const { info, success, error: showError } = useToast();
    
    try {
      console.log("MSAL: Starting initialization...");
      isLoading.value = true;
      lastAuthOperation.value = "initializing";

      // Check for persisted auth state
      const persistedState = loadPersistedAuthState();
      if (persistedState?.hasValidSession) {
        info("Restoring previous session...");
      }

      // Initialize MSAL service (this will handle config fetching)
      await msalService.initialize();
      const msalInstance = await msalService.getInstance();

      console.log("MSAL: Starting handleRedirectPromise...");
      // Now handle the redirect promise
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        console.log("MSAL: Redirect handled, setting account.", response.account);
        account.value = response.account;
        msalInstance.setActiveAccount(response.account);
        
        // Clean up transient redirect parameters (code/state) from the URL so
        // the login/redirect entry doesn't remain in browser history.
        try {
          sanitizeAuthRedirectUrl();
        } catch (e) {
          console.warn("MSAL: Failed to sanitize redirect URL:", e);
        }

        success("Successfully signed in!");
        lastAuthOperation.value = "signed_in";
      } else {
        // Check for active account if no redirect response
        const currentAccount = msalInstance.getActiveAccount();
        if (!currentAccount) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            console.log("MSAL: Setting active account from existing accounts.", accounts[0]);
            msalInstance.setActiveAccount(accounts[0]);
            account.value = accounts[0];
            info("Session restored");
          } else {
            console.log("MSAL: No active or existing accounts found.");
            account.value = null; // Ensure account is null if none found
            clearPersistedAuthState();
          }
        } else {
          console.log("MSAL: Active account already set.", currentAccount);
          account.value = currentAccount; // Ensure ref is updated
        }
      }

      // Setup user session if we have an account
      if (account.value) {
        console.log("MSAL: Establishing user session...");
        lastAuthOperation.value = "establishing_session";
        await setupUserSession();
        
        // Save successful auth state
        saveAuthState({
          hasValidSession: true,
          lastSuccessfulAuth: new Date().toISOString(),
          userEmail: account.value.username
        });
      }

      console.log("MSAL: Initialization successful.");
      isInitialized.value = true;
      initializationError.value = null;
      lastAuthOperation.value = "ready";

    } catch (error) {
      console.error("MSAL: Initialization failed:", error);
      isInitialized.value = true; // Still considered initialized, but with an error
      initializationError.value =
        error instanceof AuthError ? error : new AuthError("Initialization failed");
      account.value = null; // Clear account on init error
      lastAuthOperation.value = "failed";
      
      clearPersistedAuthState();
      
      const authError = initializationError.value;
      showError(`Authentication initialization failed: ${authError.message}`);
    } finally {
      isLoading.value = false;
    }
  })();

  return initializePromise;
}

// Remove auth redirect query params (code, state) and replace current history entry
function sanitizeAuthRedirectUrl() {
  try {
    const url = new URL(window.location.href);
    // Remove common OIDC params that appear after redirects
    const paramsToRemove = ["code", "state", "session_state", "error", "error_description"];
    let changed = false;
    paramsToRemove.forEach((p) => {
      if (url.searchParams.has(p)) {
        url.searchParams.delete(p);
        changed = true;
      }
    });
    // If we removed params, replace the current history entry with the cleaned URL
    if (changed) {
      const cleaned = url.pathname + url.search + url.hash;
      window.history.replaceState({}, document.title, cleaned);
    }
  } catch (err) {
    // Non-fatal: if URL construction fails, just skip cleanup
    console.warn("sanitizeAuthRedirectUrl error:", err);
  }
}

// --- User Session Setup ---
async function setupUserSession(): Promise<void> {
  try {
    console.log("MSAL: Setting up user session...");

    // Ensure we have an account
    if (!account.value) {
      throw new Error("No account available for session setup");
    }

    // Get API token for backend calls
    const msalInstance = await msalService.getInstance();
    const apiRequest = await msalService.getApiScopes();
    const backendToken = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account: account.value,
    });

    // Setup basic user session via backend (without Graph access)
    // Setup reduced user session via backend - returns isAuthorized and settings
    const session = await establishUserSession(backendToken.accessToken, {
      name: account.value?.name || "Unknown User"
    });

    // Expose session to the module-level reactive state
    userSession.value = session;

    // If user is not authorized, do not continue to enable Graph or fetch panels
    if (!session.isAuthorized) {
      console.log("MSAL: User is not authorized to use panels yet.");
      return;
    }

    console.log("MSAL: User session setup completed successfully.");
  } catch (error) {
    console.error("MSAL: Failed to setup user session:", error);
    // Don't throw - this shouldn't break the login flow
  }
}


// --- User-Controlled Graph Access Enable ---
async function enableUserGraphAccess(): Promise<{ success: boolean; error?: string }> {
  if (!account.value) {
    return { success: false, error: "No active account" };
  }

  try {
    console.log("MSAL: User requested Graph access - attempting consent...");

    // Try to acquire Graph token (this will trigger consent if needed)
    const msalInstance = await msalService.getInstance();
    const graphRequest = await msalService.getGraphScopes();

    try {
      await msalInstance.acquireTokenSilent({
        ...graphRequest,
        account: account.value,
      });
      console.log("MSAL: Graph token acquired successfully (consent already granted).");
    } catch (graphError: unknown) {
      console.log("MSAL: Silent Graph token acquisition failed, trying interactive...");

      // Handle interaction-required cases with redirect
      const ge = graphError as { errorCode?: string; error?: string } | null;
      const isInteractionRequired = graphError instanceof InteractionRequiredAuthError
        || (ge && (ge.errorCode === "interaction_required" || ge.error === "interaction_required"))
        || String(graphError).includes("AADSTS65001");

      if (isInteractionRequired) {
        console.log("MSAL: Starting interactive consent flow for Graph access...");
        const msalInstance = await msalService.getInstance();
        const graphRequest = await msalService.getGraphScopes();

        // Use redirect for consent (more reliable than popup)
        msalInstance.acquireTokenRedirect({ ...graphRequest, account: account.value }).catch((redirectErr) => {
          console.error("MSAL: acquireTokenRedirect for Graph failed to start:", redirectErr);
        });

        // Function will not continue after redirect - user will return to app with tokens
        return { success: false, error: "Redirecting for consent..." };
      } else {
        throw graphError; // Re-throw non-interaction errors
      }
    }

    // If we get here, we have Graph consent - now enable it on the backend
    const apiRequest = await msalService.getApiScopes();
    const backendToken = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account: account.value,
    });

    await enableGraphAccess(backendToken.accessToken);
    console.log("MSAL: Graph access successfully enabled for user.");

    return { success: true };
  } catch (error) {
    console.error("MSAL: Failed to enable Graph access:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}// --- Main Auth Composable ---
export function useAuth() {
  const { success, error, warning, info } = useToast();
  
  const isAuthenticated = computed(
    () => !!account.value && isInitialized.value && !initializationError.value
  );
  const username = computed(() => account.value?.username || "");

  const signIn = async () => {
    // Wait for initialization before attempting login
    isLoading.value = true;
    lastAuthOperation.value = "signing_in";
    
    try {
      await initializeMsal();
      if (initializationError.value) {
        console.error("MSAL login aborted due to initialization error.", initializationError.value);
        error("Unable to sign in due to initialization error");
        return;
      }
      
      info("Redirecting to sign in...");
      const msalInstance = await msalService.getInstance();
      const loginRequest = await msalService.getAuthenticationScopes();
      await msalInstance.loginRedirect(loginRequest);
    } catch (loginError) {
      console.error("Login error:", loginError);
      error(`Sign in failed: ${loginError instanceof Error ? loginError.message : "Unknown error"}`);
      lastAuthOperation.value = "sign_in_failed";
    } finally {
      isLoading.value = false;
    }
  };

  const signOut = async () => {
    isLoading.value = true;
    lastAuthOperation.value = "signing_out";
    
    try {
      // Wait for initialization before attempting logout
      await initializeMsal();
      if (initializationError.value) {
        console.error("MSAL logout aborted due to initialization error.", initializationError.value);
        // Continue with logout attempt anyway
        warning("Signing out despite initialization error");
      }
      
      const msalInstance = await msalService.getInstance();
      const currentAccount = msalInstance.getActiveAccount();
      
      if (currentAccount) {
        info("Signing out...");
        clearPersistedAuthState();
        account.value = null;
        userSession.value = null;
        msalInstance.logoutRedirect({ account: currentAccount });
      } else {
        // If no active account, clear local state
        account.value = null;
        userSession.value = null;
        clearPersistedAuthState();
        success("Signed out successfully");
        lastAuthOperation.value = "signed_out";
      }
    } catch (logoutError) {
      console.error("Logout error:", logoutError);
      error(`Sign out failed: ${logoutError instanceof Error ? logoutError.message : "Unknown error"}`);
      lastAuthOperation.value = "sign_out_failed";
    } finally {
      isLoading.value = false;
    }
  };

  const getApiToken = async (): Promise<string> => {
    // Return Promise<string>, throw on error
    // Wait for initialization
    await initializeMsal();

    if (initializationError.value) {
      console.error(
        "MSAL getApiToken aborted due to initialization error.",
        initializationError.value
      );
      throw initializationError.value; // Re-throw init error
    }
    if (!account.value) {
      console.error("MSAL getApiToken called when no account is active.");
      throw new AuthError("No active account", "NoAccountError"); // Throw specific error
    }

    try {
      const msalInstance = await msalService.getInstance();
      const apiRequest = await msalService.getApiScopes();
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account: account.value, // Use the reactive account ref's value
      });
      console.log("MSAL: Token acquired silently.");
      return tokenResponse.accessToken;
    } catch (error: unknown) {
      console.warn("MSAL: Silent token acquisition failed:", error);
      if (error instanceof InteractionRequiredAuthError) {
        console.log("MSAL: Interaction required, redirecting for interactive token acquisition.");
        // Use redirect flow which is more reliable in environments where popups are blocked
        const msalInstance = await msalService.getInstance();
        const apiRequest = await msalService.getApiScopes();
        // Use acquireTokenRedirect which will redirect the browser and resume via handleRedirectPromise
        msalInstance.acquireTokenRedirect({ ...apiRequest, account: account.value }).catch((redirectErr) => {
          // Log but don't throw - the redirect will navigate away
          console.error("MSAL: acquireTokenRedirect failed to start:", redirectErr);
        });
        // Returning a pending rejection because redirect will interrupt flow; callers should handle no-token scenario
        throw new InteractionRequiredAuthError("interaction_required", "Redirecting for interactive authentication");
      } else {
        console.error("MSAL: Token acquisition error:", error);
        throw error; // Re-throw other errors
      }
    }
  };

  const getGraphToken = async (): Promise<string> => {
    // Return Promise<string>, throw on error
    // Wait for initialization
    await initializeMsal();

    if (initializationError.value) {
      console.error(
        "MSAL getGraphToken aborted due to initialization error.",
        initializationError.value
      );
      throw initializationError.value; // Re-throw init error
    }
    if (!account.value) {
      console.error("MSAL getGraphToken called when no account is active.");
      throw new AuthError("No active account", "NoAccountError"); // Throw specific error
    }

    try {
      const msalInstance = await msalService.getInstance();
      const graphRequest = await msalService.getGraphScopes();
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...graphRequest,
        account: account.value, // Use the reactive account ref's value
      });
      console.log("MSAL: Graph token acquired silently.");
      return tokenResponse.accessToken;
    } catch (error: unknown) {
      console.warn("MSAL: Silent Graph token acquisition failed:", error);
      if (error instanceof InteractionRequiredAuthError) {
        console.log("MSAL: Interaction required for Graph token, redirecting for interactive auth.");
        const msalInstance = await msalService.getInstance();
        const graphRequest = await msalService.getGraphScopes();
        msalInstance.acquireTokenRedirect({ ...graphRequest, account: account.value }).catch((redirectErr) => {
          console.error("MSAL: acquireTokenRedirect for Graph failed to start:", redirectErr);
        });
        throw new InteractionRequiredAuthError("interaction_required", "Redirecting for interactive Graph authentication");
      } else {
        console.error("MSAL: Graph token acquisition error:", error);
        throw error; // Re-throw other errors
      }
    }
  };

  return {
    // Use readonly for state exposed from the composable
    user: readonly(account),
    isInitialized: readonly(isInitialized),
    initializationError: readonly(initializationError),
    isLoading: readonly(isLoading),
    lastAuthOperation: readonly(lastAuthOperation),
    isAuthenticated,
    username,
    login: signIn,
    logout: signOut,
    getApiToken,
    getGraphToken,
    enableGraphAccess: enableUserGraphAccess, // User-controlled Graph access enable
    initializeMsal, // Expose initialize function
    // Session info from backend
    userSession: readonly(userSession),
    isAuthorized: computed(() => !!userSession.value && userSession.value.isAuthorized === true)
  };
}
